import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { eventsConfig } from '@/config/eventsConfig';
import { trackFormSubmission } from '@/utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ListYourEventForm() {
  const [formData, setFormData] = useState({
    eventName: '',
    eventType: '',
    city: '',
    venueName: '',
    startDate: '',
    endDate: '',
    officialUrl: '',
    shortDescription: '',
    longDescription: '',
    organiserName: '',
    organiserWebsite: '',
    contactEmail: '',
    needsAccommodationHelp: false,
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const eventTypes = [
    'Conference',
    'Training',
    'Expo',
    'Workshop',
    'Seminar',
    'Trade Show',
    'Summit',
    'Other',
  ];

  const cities = eventsConfig.cities.map(city => city.name);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${API}/forms/events/list`, formData);
      
      // Track form submission
      trackFormSubmission('event_listing', {
        event_name: formData.eventName,
        event_type: formData.eventType,
        city: formData.city,
      });
      
      setStatus({
        type: 'success',
        message: 'Thank you! Your event submission has been received. Our team will review it and be in touch shortly.',
      });
      // Reset form
      setFormData({
        eventName: '',
        eventType: '',
        city: '',
        venueName: '',
        startDate: '',
        endDate: '',
        officialUrl: '',
        shortDescription: '',
        longDescription: '',
        organiserName: '',
        organiserWebsite: '',
        contactEmail: '',
        needsAccommodationHelp: false,
      });
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus({
        type: 'error',
        message: 'Sorry, there was an error submitting your event. Please try again or contact us directly.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Event Name */}
      <div>
        <Label htmlFor="eventName" className="text-sm font-medium text-gray-700 mb-2">
          Event Name <span className="text-red-600">*</span>
        </Label>
        <Input
          id="eventName"
          name="eventName"
          value={formData.eventName}
          onChange={handleChange}
          required
          className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
          placeholder="e.g., London Tech Summit 2026"
          data-testid="event-name-input"
        />
      </div>

      {/* Event Type */}
      <div>
        <Label htmlFor="eventType" className="text-sm font-medium text-gray-700 mb-2">
          Event Type <span className="text-red-600">*</span>
        </Label>
        <Select value={formData.eventType} onValueChange={(value) => handleSelectChange('eventType', value)} required>
          <SelectTrigger className="border border-gray-300 rounded-lg" data-testid="event-type-select">
            <SelectValue placeholder="Select event type" />
          </SelectTrigger>
          <SelectContent>
            {eventTypes.map((type) => (
              <SelectItem key={type} value={type.toLowerCase()}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* City and Venue */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="city" className="text-sm font-medium text-gray-700 mb-2">
            City <span className="text-red-600">*</span>
          </Label>
          <Select value={formData.city} onValueChange={(value) => handleSelectChange('city', value)} required>
            <SelectTrigger className="border border-gray-300 rounded-lg" data-testid="city-select">
              <SelectValue placeholder="Select city" />
            </SelectTrigger>
            <SelectContent>
              {cities.map((city) => (
                <SelectItem key={city} value={city.toLowerCase()}>
                  {city}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="venueName" className="text-sm font-medium text-gray-700 mb-2">
            Venue Name
          </Label>
          <Input
            id="venueName"
            name="venueName"
            value={formData.venueName}
            onChange={handleChange}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
            placeholder="e.g., ExCeL London"
            data-testid="venue-name-input"
          />
        </div>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="startDate" className="text-sm font-medium text-gray-700 mb-2">
            Start Date <span className="text-red-600">*</span>
          </Label>
          <Input
            type="date"
            id="startDate"
            name="startDate"
            value={formData.startDate}
            onChange={handleChange}
            required
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
            data-testid="start-date-input"
          />
        </div>

        <div>
          <Label htmlFor="endDate" className="text-sm font-medium text-gray-700 mb-2">
            End Date
          </Label>
          <Input
            type="date"
            id="endDate"
            name="endDate"
            value={formData.endDate}
            onChange={handleChange}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
            data-testid="end-date-input"
          />
        </div>
      </div>

      {/* Official URL */}
      <div>
        <Label htmlFor="officialUrl" className="text-sm font-medium text-gray-700 mb-2">
          Official Event URL (Eventbrite, own website, etc.)
        </Label>
        <Input
          type="url"
          id="officialUrl"
          name="officialUrl"
          value={formData.officialUrl}
          onChange={handleChange}
          className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
          placeholder="https://"
          data-testid="official-url-input"
        />
      </div>

      {/* Short Description */}
      <div>
        <Label htmlFor="shortDescription" className="text-sm font-medium text-gray-700 mb-2">
          Short Description (1-2 sentences) <span className="text-red-600">*</span>
        </Label>
        <Textarea
          id="shortDescription"
          name="shortDescription"
          value={formData.shortDescription}
          onChange={handleChange}
          required
          rows={2}
          className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
          placeholder="Brief teaser about your event"
          data-testid="short-description-input"
        />
      </div>

      {/* Long Description */}
      <div>
        <Label htmlFor="longDescription" className="text-sm font-medium text-gray-700 mb-2">
          Full Description
        </Label>
        <Textarea
          id="longDescription"
          name="longDescription"
          value={formData.longDescription}
          onChange={handleChange}
          rows={6}
          className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
          placeholder="Full marketing copy for your event"
          data-testid="long-description-input"
        />
      </div>

      {/* Organiser Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="organiserName" className="text-sm font-medium text-gray-700 mb-2">
            Organiser Name <span className="text-red-600">*</span>
          </Label>
          <Input
            id="organiserName"
            name="organiserName"
            value={formData.organiserName}
            onChange={handleChange}
            required
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
            placeholder="Your company name"
            data-testid="organiser-name-input"
          />
        </div>

        <div>
          <Label htmlFor="organiserWebsite" className="text-sm font-medium text-gray-700 mb-2">
            Organiser Website
          </Label>
          <Input
            type="url"
            id="organiserWebsite"
            name="organiserWebsite"
            value={formData.organiserWebsite}
            onChange={handleChange}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
            placeholder="https://"
            data-testid="organiser-website-input"
          />
        </div>
      </div>

      {/* Contact Email */}
      <div>
        <Label htmlFor="contactEmail" className="text-sm font-medium text-gray-700 mb-2">
          Contact Email <span className="text-red-600">*</span>
        </Label>
        <Input
          type="email"
          id="contactEmail"
          name="contactEmail"
          value={formData.contactEmail}
          onChange={handleChange}
          required
          className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
          placeholder="your@email.com"
          data-testid="contact-email-input"
        />
      </div>

      {/* Accommodation Help */}
      <div className="flex items-start space-x-3">
        <Checkbox
          id="needsAccommodationHelp"
          checked={formData.needsAccommodationHelp}
          onCheckedChange={(checked) => handleSelectChange('needsAccommodationHelp', checked)}
          data-testid="accommodation-help-checkbox"
        />
        <Label htmlFor="needsAccommodationHelp" className="text-sm text-gray-700 cursor-pointer">
          I'd like help with delegate/sponsor accommodation & meeting rooms for this event
        </Label>
      </div>

      {/* Status Messages */}
      {status.message && (
        <div
          className={`p-4 rounded-lg ${
            status.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
          data-testid="form-status-message"
        >
          {status.message}
        </div>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={loading}
        className="w-full bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 text-lg rounded-lg font-medium"
        data-testid="submit-event-button"
      >
        {loading ? 'Submitting...' : 'Submit Event Listing'}
      </Button>
    </form>
  );
}